import { memo } from 'react';

import { Category } from '@/components/Category';
import { readStringField } from '@/utils/read-string-field';
import { useCoreUIDataContext } from '@/widgets/CoreUIDataProvider/context';
import { useCoreUIDataEntry } from '@/widgets/CoreUIDataProvider/CoreUIDataProvider';

import { Fallback } from './Fallback/Fallback';
import { Loader } from './Loader/Loader';

import type { CategoryOverviewResponse } from './api-types';
import type { CategoryWidgetActionMeta, CategoryWidgetProps, MappedGroup } from './types';

export function mapCategoryResponse(response: CategoryOverviewResponse): MappedGroup[] {
  return response.categories.map((category, categoryIndex): MappedGroup => {
    const header = category.header ?? undefined;
    const headerLogMeta = header?.serverLogMeta ?? undefined;

    // categoryCode 우선, 미존재 시 index 기반 안정 fallback
    const categoryCode = readStringField(headerLogMeta, 'categoryCode');
    const titleAction = categoryCode ?? `category-${categoryIndex}`;

    const items = [...category.businesses, ...(category.themes ?? [])].map((content) => {
      const contentLogMeta = content.serverLogMeta ?? undefined;
      const businessCode = readStringField(contentLogMeta, 'businessCode');
      const themeCode = readStringField(contentLogMeta, 'themeCode');
      const itemAction = businessCode ?? themeCode ?? content.title;

      return {
        itemAction,
        label: content.title,
        meta: {
          action: content.action,
          serverLogMeta: content.serverLogMeta ?? undefined,
        },
      };
    });

    return {
      groupKey: titleAction,
      title: header?.type === 'TITLE' ? (header.title ?? undefined) : undefined,
      iconUrl: header?.imageUrl ?? undefined,
      navigable: header?.action !== undefined && header.action.web.length > 0,
      titleAction,
      titleMeta: {
        action: header?.action,
        serverLogMeta: header?.serverLogMeta ?? undefined,
      },
      items,
    };
  });
}

/**
 * 카테고리 메뉴 — API 응답 자동 매핑 + Provider 기반 lazy fetch.
 *
 * # 동작 매트릭스
 *
 * | Provider | data prop | entry 상태 | 결과 |
 * |---|---|---|---|
 * | 있음 | 명시 | (무관)        | prop 데이터 렌더 (fetch skip — `enabled: false`) |
 * | 있음 | 미명시 | success      | context 데이터 렌더 |
 * | 있음 | 미명시 | loading/idle | 내장 skeleton |
 * | 있음 | 미명시 | error        | 내장 error UI (retry button) |
 * | 없음 | 명시   | n/a          | prop 데이터 렌더 (dumb 모드, BC) |
 * | 없음 | 미명시 | n/a          | 내장 skeleton + dev warn |
 *
 * # 사용
 *
 * ```tsx
 * <CoreUIDataProvider env="prod" platform="Web">
 *   <CategoryWidget
 *     onAction={(action, meta) => {
 *       clientLog(meta.serverLogMeta);
 *       if (meta.action?.web) location.href = meta.action.web;
 *     }}
 *   />
 * </CoreUIDataProvider>
 * ```
 *
 * @see plan/category-widget-design.md — Q7, Q9, Q12, Q17
 */
export const CategoryWidget = memo(function CategoryWidget(props: CategoryWidgetProps) {
  const { data, onAction, activeAction, defaultActiveAction, 'aria-label': ariaLabel, className } = props;

  const ctx = useCoreUIDataContext();
  // host 가 data prop 으로 직접 데이터를 넘긴 경우 fetch 트리거 skip.
  // Provider 부재 시도 enabled=false 로 처리해 hook 내부 dev warn 미발생 (외부 widget 의 warn 으로 위임).
  const shouldFetch = ctx !== null && data === undefined;
  const { entry, retry } = useCoreUIDataEntry('category', { enabled: shouldFetch });

  if (process.env.NODE_ENV !== 'production' && ctx === null && data === undefined) {
    // eslint-disable-next-line no-console
    console.warn(
      '[core-ui] CategoryWidget: no <CoreUIDataProvider> ancestor and no `data` prop. ' +
        'Provide one of: wrap in <CoreUIDataProvider> (auto-fetch), or pass `data` prop directly.',
    );
  }

  // 우선순위: data prop > context entry.success → 그 외 default UI
  const renderData = data ?? (entry.status === 'success' ? entry.data : undefined);

  if (renderData === undefined) {
    return entry.status === 'error' ? <Fallback onRetry={retry} /> : <Loader />;
  }

  // 정규화된 응답을 dumb Category compound 트리로 렌더.
  // 매핑 함수가 meta 에 typed shape 으로 넣어두므로 dumb 의 untyped onAction 을 widget 의 typed onAction 으로 cast.
  const groups = mapCategoryResponse(renderData);

  return (
    <Category
      activeAction={activeAction}
      aria-label={ariaLabel}
      className={className}
      defaultActiveAction={defaultActiveAction}
      onAction={(action, meta, e) => {
        onAction?.(action, meta as CategoryWidgetActionMeta, e);
      }}
    >
      {groups.map((group) => (
        <Category.Group key={group.groupKey}>
          <Category.GroupTitle
            action={group.titleAction}
            icon={group.iconUrl}
            label={group.title}
            meta={group.titleMeta as Record<string, unknown>}
            navigable={group.navigable}
          />
          {group.items.map((item) => (
            <Category.Item
              action={item.itemAction}
              key={`${group.groupKey}-${item.itemAction}`}
              label={item.label}
              meta={item.meta as Record<string, unknown>}
            />
          ))}
        </Category.Group>
      ))}
    </Category>
  );
});

CategoryWidget.displayName = 'CategoryWidget';
